import React, { useMemo } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import { format } from 'date-fns';
import { parseLocalDate } from '../../utils/formatters';
import { es } from 'date-fns/locale';
import { chartPalette } from './chartPalette';

interface SalesDataPoint {
  date: string;
  orders: number;
  total: number;
}

type DashboardMode = 'light' | 'dark';

interface SalesTrendChartProps {
  data?: SalesDataPoint[];
  loading?: boolean;
  mode?: DashboardMode;
}

const ChartContainer = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'mode',
})<{ mode?: DashboardMode }>(({ mode = 'dark' }) => {
  const isLight = mode === 'light';
  return {
    padding: '24px',
    borderRadius: '16px',
    position: 'relative',
    overflow: 'hidden',
    ...(isLight
      ? {
          background: '#ffffff',
          border: '1px solid #bae6fd',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
          '&::before': { content: 'none' },
        }
      : {
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
          background:
            'linear-gradient(135deg, rgba(15, 23, 42, 0.8) 0%, rgba(30, 41, 59, 0.8) 100%)',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              'radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.05) 0%, transparent 50%)',
            pointerEvents: 'none',
            zIndex: 0,
          },
        }),
  };
});

const HeaderContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '24px',
  position: 'relative',
  zIndex: 1,
});

const Title = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'mode',
})<{ mode?: DashboardMode }>(({ mode = 'dark' }) => ({
  fontSize: '20px',
  fontWeight: 600,
  color: mode === 'light' ? '#0f172a' : '#ffffff',
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  letterSpacing: '-0.01em',
}));

const CustomTooltip = ({ active, payload, isLight }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const dateStr = data.date || payload[0].payload.dateLabel;
    return (
      <Box
        sx={
          isLight
            ? {
                background: '#ffffff',
                border: '1px solid #e0f2fe',
                borderRadius: '12px',
                padding: '12px 16px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              }
            : {
                background:
                  'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
                padding: '12px 16px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                backdropFilter: 'blur(10px)',
              }
        }
      >
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 600,
            color: isLight ? '#64748b' : '#ffffff',
            marginBottom: '8px',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          }}
        >
          {dateStr ? format(parseLocalDate(dateStr), 'EEEE, d MMMM', { locale: es }) : ''}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 600,
              color: chartPalette.accent.teal,
              fontFamily:
                'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            }}
          >
            Total: Bs. {data.total.toLocaleString('es-BO')}
          </Typography>
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 500,
              color: isLight ? '#64748b' : 'rgba(255, 255, 255, 0.7)',
              fontFamily:
                'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            }}
          >
            {data.orders} {data.orders === 1 ? 'venta' : 'ventas'}
          </Typography>
        </Box>
      </Box>
    );
  }
  return null;
};

export const SalesTrendChart: React.FC<SalesTrendChartProps> = ({
  data,
  loading = false,
  mode = 'dark',
}) => {
  const isLight = mode === 'light';
  const gridStroke = isLight ? '#e2e8f0' : 'rgba(255, 255, 255, 0.05)';
  const axisStroke = isLight ? '#94a3b8' : 'rgba(255, 255, 255, 0.4)';
  const tickFill = isLight ? '#64748b' : 'rgba(255, 255, 255, 0.6)';

  const chartData = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const formattedData = useMemo(() => {
    const dateFormat = 'd MMM';
    return chartData.map((item) => ({
      ...item,
      dateLabel: item.date ? format(parseLocalDate(item.date), dateFormat, { locale: es }) : '',
    }));
  }, [chartData]);

  if (loading) {
    return (
      <ChartContainer mode={mode}>
        <Box
          sx={{ height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Typography sx={{ color: isLight ? '#94a3b8' : 'rgba(255, 255, 255, 0.5)' }}>
            Cargando...
          </Typography>
        </Box>
      </ChartContainer>
    );
  }

  if (chartData.length === 0) {
    return (
      <ChartContainer mode={mode}>
        <HeaderContainer>
          <Title mode={mode}>Ventas en dinero</Title>
        </HeaderContainer>
        <Box
          sx={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Typography sx={{ color: isLight ? '#94a3b8' : 'rgba(255, 255, 255, 0.5)' }}>
            No hay datos en el periodo seleccionado
          </Typography>
        </Box>
      </ChartContainer>
    );
  }

  return (
    <ChartContainer mode={mode}>
      <HeaderContainer>
        <Title mode={mode}>Ventas en dinero</Title>
      </HeaderContainer>

      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <ResponsiveContainer width="100%" height={400}>
          <AreaChart data={formattedData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="colorVentasDinero" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={chartPalette.gradients.sales[0]} stopOpacity={0.35} />
                <stop offset="95%" stopColor={chartPalette.gradients.sales[1]} stopOpacity={0.08} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
            <XAxis
              dataKey="dateLabel"
              stroke={axisStroke}
              style={{ fontSize: '12px', fontFamily: 'Inter, sans-serif' }}
              tick={{ fill: tickFill }}
              axisLine={{ stroke: axisStroke }}
            />
            <YAxis
              stroke={axisStroke}
              style={{ fontSize: '12px', fontFamily: 'Inter, sans-serif' }}
              tick={{ fill: tickFill }}
              axisLine={{ stroke: axisStroke }}
              tickFormatter={(v) => Number(v).toLocaleString('es-BO')}
              label={{
                value: 'Bs.',
                angle: -90,
                position: 'insideLeft',
                style: {
                  textAnchor: 'middle',
                  fill: tickFill,
                  fontSize: '12px',
                  fontFamily: 'Inter, sans-serif',
                },
              }}
            />
            <Tooltip content={<CustomTooltip isLight={isLight} />} />
            <Area
              type="monotone"
              dataKey="total"
              stroke="url(#colorVentasDinero)"
              strokeWidth={3}
              fill="url(#colorVentasDinero)"
              dot={{
                fill: chartPalette.accent.cyan,
                r: 4,
                strokeWidth: 2,
                stroke: isLight ? '#ffffff' : '#ffffff',
              }}
              activeDot={{
                r: 6,
                fill: chartPalette.accent.teal,
                strokeWidth: 2,
                stroke: '#ffffff',
              }}
              animationDuration={1000}
              animationEasing="ease-out"
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </ChartContainer>
  );
};
